/**
 * Validation for the Settings › Manufacturing › Live costing "Check
 * settings" edit form (item C3, `04_handover_2026-09-12.md`). Pure and
 * platform-neutral so the page can bind an error message under the
 * offending field and disable Confirm until the draft is valid, without
 * duplicating the rules.
 */

export interface CheckSettingsDraftValues {
  matchesTolerancePct: number;
  mismatchLimitPct: number;
  sameDirectionCount: number;
  sameDirectionWindow: number;
  checkWindowWeeks: number;
  weeklyCheckBudget: number;
  /** The per-order guard (cost-engine-productionisation spec 03 §5.9): a
   *  served price flags for review only when it differs from Tharstern's by
   *  more than *both* this percentage and this pence floor — grouped as its
   *  own "Order guard" panel section (spec 07 §15 P-1). */
  orderGuardPct: number;
  orderGuardFloorPence: number;
  /** The line floor (product owner, 19 Sep 2026): a cost line whose absolute
   *  difference is under this many pence never trips a check, applied before
   *  the percentage test. 0 turns the floor off, so unlike its siblings this
   *  one is validated as non-negative rather than strictly positive. */
  lineFloorPence: number;
  /** Rolling-run pacing (Ben, 24 Sep 2026): the minimum gap, in seconds,
   *  between Tharstern estimate requests in a rolling run, start to start.
   *  0 turns pacing off, so — like the line floor — it is validated as
   *  non-negative rather than strictly positive. */
  mintIntervalSeconds: number;
  /** The most mints one (hourly) rolling run attempts; the weekly check
   *  budget still bounds the week. */
  runMintCap: number;
  /** How long, in seconds, a rolling run waits after a Tharstern timeout
   *  before its next estimate request (Ben, 24 Sep 2026). 0 turns the
   *  back-off off, so it is validated as non-negative. */
  timeoutBackoffSeconds: number;
}

export type CheckSettingsField = keyof CheckSettingsDraftValues;

export type CheckSettingsFieldErrors = Partial<
  Record<CheckSettingsField, string>
>;

const WHOLE_NUMBER_FIELDS: CheckSettingsField[] = [
  "sameDirectionCount",
  "sameDirectionWindow",
  "checkWindowWeeks",
  "weeklyCheckBudget",
  "orderGuardFloorPence",
  "lineFloorPence",
  "mintIntervalSeconds",
  "runMintCap",
  "timeoutBackoffSeconds",
];

/** Fields where 0 is a meaningful setting, not an empty one — the line floor
 *  at 0 means "no floor", which is a choice an admin may legitimately make. */
const NON_NEGATIVE_FIELDS: CheckSettingsField[] = [
  "lineFloorPence",
  "mintIntervalSeconds",
  "timeoutBackoffSeconds",
];

/** What each field's error asks for (docs/ui-wording.md: a full sentence
 *  naming the thing to enter). `range` breaks the field's lower bound: above 0
 *  for most, 0 or more where 0 turns the setting off. `whole` is a fraction
 *  in a field of whole checks, weeks, mints, seconds or pence. */
const FIELD_MESSAGES: Record<
  CheckSettingsField,
  { range: string; whole?: string }
> = {
  matchesTolerancePct: { range: "Enter a matches tolerance above 0%." },
  mismatchLimitPct: { range: "Enter a mismatch limit above 0%." },
  sameDirectionCount: {
    range: "Enter a same-direction count of 1 or more.",
    whole: "Enter the same-direction count as a whole number of checks.",
  },
  sameDirectionWindow: {
    range: "Enter a same-direction window of 1 or more checks.",
    whole: "Enter the same-direction window as a whole number of checks.",
  },
  checkWindowWeeks: {
    range: "Enter a check window of 1 or more weeks.",
    whole: "Enter the check window as a whole number of weeks.",
  },
  weeklyCheckBudget: {
    range: "Enter a weekly check budget of 1 or more checks.",
    whole: "Enter the weekly check budget as a whole number of checks.",
  },
  orderGuardPct: { range: "Enter an order guard threshold above 0%." },
  orderGuardFloorPence: {
    range: "Enter an order guard floor above £0.00.",
    whole: "Enter the order guard floor to the nearest penny.",
  },
  lineFloorPence: {
    range: "Enter a line floor of £0.00 or more. £0.00 turns the floor off.",
    whole: "Enter the line floor to the nearest penny.",
  },
  mintIntervalSeconds: {
    range: "Enter a mint interval of 0 seconds or more. 0 turns pacing off.",
    whole: "Enter the mint interval as a whole number of seconds.",
  },
  runMintCap: {
    range: "Enter a per-run cap of 1 or more mints.",
    whole: "Enter the per-run cap as a whole number of mints.",
  },
  timeoutBackoffSeconds: {
    range: "Enter a back-off of 0 seconds or more. 0 turns the back-off off.",
    whole: "Enter the back-off as a whole number of seconds.",
  },
};

/** The longest a paced run may be configured to take (`runMintCap` ×
 *  `mintIntervalSeconds`): 3 h, inside the rolling unit's 4 h limit — the
 *  same bound `cost_check_module`'s `update_check_settings` enforces
 *  (`MAX_PACED_RUN_SECONDS`), mirrored so Confirm disables before the
 *  server has to say so. */
export const MAX_PACED_RUN_SECONDS = 3 * 60 * 60;

const ALL_FIELDS: CheckSettingsField[] = [
  "matchesTolerancePct",
  "mismatchLimitPct",
  "sameDirectionCount",
  "sameDirectionWindow",
  "checkWindowWeeks",
  "weeklyCheckBudget",
  "orderGuardPct",
  "orderGuardFloorPence",
  "runMintCap",
];

/**
 * Every field in `ALL_FIELDS` must be a positive number (the line floor, the
 * mint interval and the back-off may be 0, which turns them off); the counts must be
 * whole numbers (a fractional week or a fractional check makes no sense);
 * the mismatch limit must sit above the matches tolerance (otherwise no
 * percentage difference could ever land in the "small differences" band);
 * the same-direction count can never exceed its own window; and a paced run
 * (`runMintCap` × `mintIntervalSeconds`) must fit in `MAX_PACED_RUN_SECONDS`.
 * Returns an empty object when the draft is valid.
 */
export function validateCheckSettingsDraft(
  draft: CheckSettingsDraftValues,
): CheckSettingsFieldErrors {
  const errors: CheckSettingsFieldErrors = {};

  for (const field of ALL_FIELDS) {
    const value = draft[field];
    if (!(value > 0)) {
      errors[field] = FIELD_MESSAGES[field].range;
    }
  }

  for (const field of NON_NEGATIVE_FIELDS) {
    if (!(draft[field] >= 0)) {
      errors[field] = FIELD_MESSAGES[field].range;
    }
  }

  for (const field of WHOLE_NUMBER_FIELDS) {
    const whole = FIELD_MESSAGES[field].whole;
    if (!errors[field] && whole && !Number.isInteger(draft[field])) {
      errors[field] = whole;
    }
  }

  if (
    !errors.mismatchLimitPct &&
    !errors.matchesTolerancePct &&
    draft.mismatchLimitPct <= draft.matchesTolerancePct
  ) {
    errors.mismatchLimitPct =
      "The mismatch limit must be greater than the matches tolerance.";
  }

  if (
    !errors.sameDirectionCount &&
    !errors.sameDirectionWindow &&
    draft.sameDirectionCount > draft.sameDirectionWindow
  ) {
    errors.sameDirectionCount =
      "The same-direction count can't be more than the same-direction window.";
  }

  if (
    !errors.runMintCap &&
    !errors.mintIntervalSeconds &&
    draft.runMintCap * draft.mintIntervalSeconds > MAX_PACED_RUN_SECONDS
  ) {
    errors.runMintCap =
      "The per-run cap times the mint interval must be 3 hours or less, or the rolling run will be stopped before it finishes.";
  }

  return errors;
}

export function isCheckSettingsDraftValid(
  draft: CheckSettingsDraftValues,
): boolean {
  return Object.keys(validateCheckSettingsDraft(draft)).length === 0;
}
